import { FC, ReactNode, useEffect, useState } from "react";
import {
    Button,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
} from "antd";

import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { DescWrap } from "../../style";
import RequirementDescChild from "../../pages/Detail/components/RequirementDescChild";
import { QuotationRequestsParams } from "@/apis/requirement";
import SText from "@/styles/SText";
import SelectSupplierPrice, { PriceOption } from "@/components/Selector/SelectSupplierPrice";
import { QuotationRequest, Requirement, RequirementItem } from "@/types/requirement";
import { divided, multi } from "@/utils/calc";
interface HeadDesc {
    key: string;
    label: string;
    children: ReactNode;
}
interface AddQuotationProps {
    detail: RequirementItem;
    requirement?: Requirement;
    onCancel: () => void;
    formData?: QuotationRequest;
    onSubmit?: (values: QuotationRequestsParams) => void;
}

const AddQuotation: FC<AddQuotationProps> = ({
    detail,
    requirement,
    formData,
    onCancel,
    onSubmit
}) => {
    const { t } = useTranslation();

    const [disableMpq, setDisableMpq] = useState<boolean>(true);
    const [edit, setEdit] = useState<boolean>(false);
    const [selectedSupPrice, setSelectedSupPrice] = useState<
        PriceOption | undefined
    >(undefined);

    const [form] = Form.useForm<QuotationRequestsParams>();

    const getProductDescList: () => HeadDesc[] = () => {
        let list = [
            {
                key: "code",
                label: t("product.code"),
                children: <SText text={detail.product?.code || ""} />,
            },
            {
                key: "name",
                label: t("product.name"),
                children: <SText text={detail.product?.name || ""} />,
            },
            {
                key: "uom",
                label: t("product.uom"),
                children: <SText text={detail.product?.uom || ""} />,
            },
            {
                key: "model",
                label: t("product.brand1"),
                children: <SText text={detail.product?.model || ""} />,
            },
        ];
        return list;
    };
    const getSupploierPiceDescList: (
        data: PriceOption | undefined
    ) => HeadDesc[] = (data: PriceOption | undefined) => {
        let list = [
            {
                key: "price",
                label: t("requirement.rulePrice"),
                children: data ? <SText text={data.price || ""} /> : <></>,
            },
            {
                key: "days",
                label: t("requirement.ruleDays"),
                children: data ? <SText text={String(data.days || "")} /> : <></>,
            },
            {
                key: "mpq",
                label: t("requirement.ruleMpq"),
                children: data ? <SText text={String(data.mpq || "")} /> : <></>,
            },
            {
                key: "spareRate",
                label: t("purchase.spareRate"),
                children: data ? <SText text={data.spareRate} /> : <></>,
            },
        ];
        return list;
    };

    useEffect(() => {
        if (formData) {
            form.setFieldsValue({
                deadline: formData.deadline,
                strs: [formData.priceId],
                supplierPrice: Number(formData?.supplierPrice ?? 0) || null,
                days: formData.days || undefined,
                mpq: formData.mpq || '',
                supplierTime: formData.supplierTime,
                spareRate: formData.spareRate,
                spareCount: (formData?.spareCount || 0) + ''
            })
        } else {
            if (requirement) form.setFieldsValue({
                supplierTime: requirement?.mrd || undefined
            })
        }
    }, [formData, requirement])

    useEffect(() => {
        if (edit && selectedSupPrice) {
            form.setFieldsValue({
                strs: [selectedSupPrice.value],
                supplierPrice: Number(selectedSupPrice?.price ?? 0) || null,
                days: selectedSupPrice.days || undefined,
                mpq: selectedSupPrice.mpq || '',
                spareCount: Math.ceil(Number(multi(divided(selectedSupPrice?.spareRate || '', '100'), detail.purCount) || '')).toString()
            })
        }
    }, [selectedSupPrice, edit, detail])

    const handleFinish = (values: QuotationRequestsParams) => {
        onSubmit && onSubmit({
            ...values,
            spareRate: multi(divided(values.spareCount ?? '', detail.purCount), '100')
        })
    }

    return (
        <Modal
            maskClosable={false}
            open={true}
            onCancel={onCancel}
            title={t("requirement.qutotationProgress")}
            width={800}
            footer={
                <>
                    <Row justify="end">
                        <Button
                            type="link"
                            danger
                            onClick={onCancel}
                            style={{ marginRight: "20px" }}
                        >
                            {t("common.cancel")}
                        </Button>
                        <Button type="primary" onClick={() => form.submit()}>
                            {t("common.confirm")}
                        </Button>
                    </Row>
                </>
            }
        >
            <DescWrap>
                {getProductDescList().map((item) => (
                    <RequirementDescChild
                        key={item.key}
                        label={item.label}
                        children={item.children}
                    />
                ))}
            </DescWrap>
            <Divider style={{ borderBlockStart: "#172B4D" }}>
                {t("requirement.requirementInfo")}
            </Divider>
            <DescWrap>
                {getSupploierPiceDescList(selectedSupPrice).map((item) => (
                    <RequirementDescChild
                        key={item.key}
                        label={item.label}
                        children={item.children}
                    />
                ))}
            </DescWrap>
            <Form<QuotationRequestsParams>
                form={form}
                onFinish={handleFinish}
                size="middle"
                style={{ marginTop: "20px" }}
            >
                <Form.Item
                    label={t("requirement.supplier")}
                    name="strs"
                    rules={[
                        { required: true, message: t("requirement.plSeSupplier") },
                    ]}
                >
                    <SelectSupplierPrice
                        productId={detail.productId}
                        onChange={(_, data) => {
                            setEdit(true)
                            setSelectedSupPrice(data || undefined);
                            if (data && data?.mpq) setDisableMpq(false);
                            else setDisableMpq(true);
                        }}
                    />
                </Form.Item>
                <Form.Item
                    label={t("requirement.price")}
                    name="supplierPrice"
                    rules={[
                        { required: true, message: t("requirement.inputPrice") },
                        () => ({
                            validator(_, value) {
                                if (!value) {
                                    return Promise.reject();
                                }
                                if (
                                    value &&
                                    selectedSupPrice &&
                                    selectedSupPrice.price &&
                                    value <= Number(selectedSupPrice.price)
                                ) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(
                                    new Error(t("requirement.priceTip"))
                                );
                            },
                            warningOnly: true,
                        }),
                    ]}
                >
                    <InputNumber changeOnWheel style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                    label={t("requirement.days")}
                    name="days"
                    rules={[
                        () => ({
                            validator(_, value) {
                                if (!value) {
                                    return Promise.reject();
                                }
                                if (
                                    value &&
                                    selectedSupPrice &&
                                    selectedSupPrice.days &&
                                    value <= selectedSupPrice.days
                                ) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(
                                    new Error(t("requirement.daysTip"))
                                );
                            },
                            warningOnly: true,
                        }),
                    ]}
                >
                    <InputNumber changeOnWheel
                        precision={0}
                        max={999}
                        style={{ width: "100%" }}
                    />
                </Form.Item>
                <Form.Item
                    label={t("requirement.ddlTime")}
                    name="supplierTime"
                    validateDebounce={1000}
                    rules={[
                        { required: true, message: t("requirement.inputDdlTime") },
                    ]}
                    getValueProps={(value) => ({
                        value: value && dayjs(Number(value)),
                    })}
                    normalize={(value) => value && dayjs(value).valueOf()}
                >
                    <DatePicker
                        placeholder={t("requirement.ddlTimeTip")}
                        style={{ width: "100%" }}
                    />
                </Form.Item>
                <Form.Item
                    label={t("requirement.mpq")}
                    name="mpq"
                    rules={[
                        () => ({
                            validator(_, value) {
                                if (!value) {
                                    return Promise.reject();
                                }
                                if (
                                    value &&
                                    selectedSupPrice &&
                                    selectedSupPrice.mpq &&
                                    value <= selectedSupPrice.mpq
                                ) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(t("requirement.mpqTip")));
                            },
                            warningOnly: true,
                        }),
                    ]}
                >
                    <Input disabled={disableMpq} />
                </Form.Item>
                <Form.Item label={t("purchase.spareCount")} name="spareCount">
                    <InputNumber changeOnWheel
                        min={0}
                        step={1}
                        precision={0}
                        placeholder={t("purchase.spareCountPl")}
                        style={{ width: "100%" }}
                    />
                </Form.Item>
                <Form.Item label={t("purchase.orderDesc")} name="remark">
                    <Input.TextArea placeholder={t("purchase.plOrderDesc")} autoSize={{ minRows: 2, maxRows: 2 }} maxLength={2000} showCount />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddQuotation;
